import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PanelOverlay from '../rendering/Panels/Renderers/PanelOverlay';
import styles from './PlotPanel.module.scss';
import { PanelCommonProps } from 'config/panelConfig';
import { useAnalysisPage } from '@features/analysis/useAnalysisPage';
import { usePanelContainer } from '../rendering/Panels/usePanelContainer';
import LineChart, {
  DataPoint,
  SelectedIndices as ChartSelectedIndices,
} from '@features/analysis/analysisChart/LineChart';
import HistogramChart from '@features/analysis/analysisChart/HistogramChart';
import { useBrowserPage } from '@features/browser/useBrowserPage';
import {
  MarkerLinesMoveRender,
  useBufferPool,
} from '../rendering/Panels/Renderers/vtkManagers';
import { BranchInfo } from 'utils/FunctionUtilites';
import DualLineChart from '@features/analysis/analysisChart/DualLineChart';
import FFRChart, {
  FFR_SHARED_MARGIN,
} from '@features/analysis/analysisChart/FFRChart';
import { getDisplayRuntimeProfile } from 'config/displayRuntime';
import {
  renderers,
  scheduleRender,
} from '@features/analysis/rendering/Panels/renderState';
import PlaqueChart from '@features/analysis/analysisChart/PlaqueChart';
import vtkPolyData from '@kitware/vtk.js/Common/DataModel/PolyData';
import { handleNodeChange } from '@features/analysis/rendering/Panels/Renderers/vtkManagers';
import { useConfig } from 'config/useConfig';
import { normalizeLesionStackSpacingMm } from 'config/config';
import { getConfiguredCprCrossSectionImageMode } from '@features/analysis/rendering/Panels/cprImageModeConfig';
import * as d3 from 'd3';

type MarkerKey = 'proximal' | 'mid' | 'distal' | null;

type FfrGraphNodeData = {
  ffr: DataPoint[];
  area: DataPoint[];
  diameter: DataPoint[];
};

const getFiniteDomain = (
  data: DataPoint[],
  fallback: [number, number],
): [number, number] => {
  const values = data.map((d) => d.y).filter((v) => Number.isFinite(v));
  if (!values.length) return fallback;

  const min = Math.min(...values);
  const max = Math.max(...values);

  if (!Number.isFinite(min) || !Number.isFinite(max)) return fallback;

  if (min === max) {
    const pad = Math.max(0.1, Math.abs(min) * 0.1);
    return [min - pad, max + pad];
  }

  return [min, max];
};

const PLAQUE_CPR_STACK_CROSS_SECTION_COUNT = 7;
const PLAQUE_CPR_STACK_MIDDLE_COUNT = PLAQUE_CPR_STACK_CROSS_SECTION_COUNT - 2;
const PLAQUE_CPR_STACK_LENGTH_EPSILON_MM = 1e-3;
type LesionStackLocalIndex = number | null;

function getLesionStackCrossSectionRendererId(mainIndex: number, stackSlot: number) {
  if (stackSlot <= 0) return `CrossSection.${mainIndex}.0`;
  if (stackSlot >= PLAQUE_CPR_STACK_CROSS_SECTION_COUNT - 1) {
    return `CrossSection.${mainIndex}.2`;
  }
  return `CrossSection.3.${stackSlot - 1}`;
}

function findNearestLengthIndex(
  lengthArray: ArrayLike<number>,
  targetLength: number,
  startIndex: number,
  endIndex: number,
) {
  const last = lengthArray.length - 1;
  const from = Math.max(0, Math.min(last, Math.min(startIndex, endIndex)));
  const to = Math.max(0, Math.min(last, Math.max(startIndex, endIndex)));
  let bestIndex = from;
  let bestDistance = Number.POSITIVE_INFINITY;

  for (let index = from; index <= to; index += 1) {
    const length = Number(lengthArray[index]);
    if (!Number.isFinite(length)) continue;
    const distance = Math.abs(length - targetLength);
    if (distance < bestDistance) {
      bestDistance = distance;
      bestIndex = index;
    }
  }

  return bestIndex;
}

function getLesionStackMiddleCapacity(
  proximalLength: number,
  distalLength: number,
  spacingMm: number,
) {
  const distance = Math.abs(distalLength - proximalLength);
  if (!Number.isFinite(distance)) return 0;
  return Math.max(
    0,
    Math.min(
      PLAQUE_CPR_STACK_MIDDLE_COUNT,
      Math.floor(
        (distance - PLAQUE_CPR_STACK_LENGTH_EPSILON_MM) /
          spacingMm,
      ),
    ),
  );
}

function isLesionStackLengthBetween(
  value: number,
  proximalLength: number,
  distalLength: number,
) {
  return proximalLength <= distalLength
    ? value > proximalLength && value < distalLength
    : value < proximalLength && value > distalLength;
}

function buildLesionStackLocalIndices(
  lengthArray: ArrayLike<number> | null | undefined,
  proximalIndex: number,
  middleAnchorIndex: number,
  distalIndex: number,
  spacingMm: number,
): LesionStackLocalIndex[] {
  if (!lengthArray?.length) return [];
  if (!Number.isFinite(proximalIndex) || !Number.isFinite(distalIndex)) {
    return [];
  }

  const last = lengthArray.length - 1;
  const proximal = Math.max(0, Math.min(last, Math.round(proximalIndex)));
  const distal = Math.max(0, Math.min(last, Math.round(distalIndex)));
  const proximalLength = Number(lengthArray[proximal]);
  const distalLength = Number(lengthArray[distal]);

  if (!Number.isFinite(proximalLength) || !Number.isFinite(distalLength)) {
    return [
      proximal,
      ...Array.from({ length: PLAQUE_CPR_STACK_MIDDLE_COUNT }, () => null),
      distal,
    ];
  }

  const direction = distalLength >= proximalLength ? 1 : -1;
  const middleCapacity = getLesionStackMiddleCapacity(
    proximalLength,
    distalLength,
    spacingMm,
  );
  const indices: LesionStackLocalIndex[] = [proximal];

  if (middleCapacity <= 0) {
    indices.push(
      ...Array.from({ length: PLAQUE_CPR_STACK_MIDDLE_COUNT }, () => null),
    );
    indices.push(distal);
    return indices;
  }

  const middleAnchor = Math.max(
    0,
    Math.min(last, Math.round(middleAnchorIndex)),
  );
  const fallbackAnchorLength =
    proximalLength + direction * spacingMm;
  const candidateAnchorLength = Number(lengthArray[middleAnchor]);
  let anchorLength = Number.isFinite(candidateAnchorLength)
    ? candidateAnchorLength
    : fallbackAnchorLength;
  const shouldUseFallbackAnchor =
    !isLesionStackLengthBetween(anchorLength, proximalLength, distalLength) ||
    (direction > 0
      ? anchorLength < fallbackAnchorLength
      : anchorLength > fallbackAnchorLength);
  if (shouldUseFallbackAnchor) {
    anchorLength = fallbackAnchorLength;
  }

  for (let slot = 0; slot < PLAQUE_CPR_STACK_MIDDLE_COUNT; slot += 1) {
    if (slot >= middleCapacity) {
      indices.push(null);
      continue;
    }
    const targetLength =
      anchorLength + direction * spacingMm * slot;
    if (
      !isLesionStackLengthBetween(targetLength, proximalLength, distalLength)
    ) {
      indices.push(null);
      continue;
    }
    indices.push(findNearestLengthIndex(lengthArray, targetLength, proximal, distal));
  }

  indices.push(distal);
  return indices;
}

function isLesionStackMiddleAnchorLocalAllowed(
  lengthArray: ArrayLike<number> | null | undefined,
  proximalIndex: number,
  middleAnchorIndex: number,
  distalIndex: number,
  spacingMm: number,
) {
  if (!lengthArray?.length) return false;
  if (
    !Number.isFinite(proximalIndex) ||
    !Number.isFinite(middleAnchorIndex) ||
    !Number.isFinite(distalIndex)
  ) {
    return false;
  }
  const last = lengthArray.length - 1;
  const proximal = Math.max(0, Math.min(last, Math.round(proximalIndex)));
  const middle = Math.max(0, Math.min(last, Math.round(middleAnchorIndex)));
  const distal = Math.max(0, Math.min(last, Math.round(distalIndex)));
  const proximalLength = Number(lengthArray[proximal]);
  const middleLength = Number(lengthArray[middle]);
  const distalLength = Number(lengthArray[distal]);
  if (
    !Number.isFinite(proximalLength) ||
    !Number.isFinite(middleLength) ||
    !Number.isFinite(distalLength)
  ) {
    return false;
  }
  const middleCapacity = getLesionStackMiddleCapacity(
    proximalLength,
    distalLength,
    spacingMm,
  );
  if (middleCapacity <= 0) return false;
  const direction = distalLength >= proximalLength ? 1 : -1;
  for (let slot = 0; slot < middleCapacity; slot += 1) {
    const targetLength =
      middleLength + direction * spacingMm * slot;
    if (
      !isLesionStackLengthBetween(targetLength, proximalLength, distalLength)
    ) {
      return false;
    }
  }
  return true;
}

function clampLesionStackHiddenMiddleLocalIndex(
  targetIndex: number,
  proximalIndex: number,
  distalIndex: number,
  lastIndex: number,
) {
  const roundedTarget = Math.round(targetIndex);
  const proximal = Math.max(0, Math.min(lastIndex, Math.round(proximalIndex)));
  const distal = Math.max(0, Math.min(lastIndex, Math.round(distalIndex)));
  const lowerBound = Math.min(proximal, distal) + 1;
  const upperBound = Math.max(proximal, distal) - 1;

  if (lowerBound <= upperBound) {
    return Math.max(lowerBound, Math.min(upperBound, roundedTarget));
  }

  return Math.max(
    Math.min(proximal, distal),
    Math.min(Math.max(proximal, distal), roundedTarget),
  );
}

function resolveLesionStackHiddenMiddleLocalIndex(
  lengthArray: ArrayLike<number> | null | undefined,
  proximalIndex: number,
  distalIndex: number,
  fallbackLastIndex: number,
) {
  if (!Number.isFinite(proximalIndex) || !Number.isFinite(distalIndex)) {
    return null;
  }

  const lastIndex = Math.max(
    0,
    Math.min(
      Number.isFinite(fallbackLastIndex) ? Math.max(0, fallbackLastIndex - 1) : 0,
      (lengthArray?.length ?? 0) > 0 ? lengthArray!.length - 1 : Number.POSITIVE_INFINITY,
    ),
  );

  if (lengthArray?.length) {
    const last = lengthArray.length - 1;
    const proximal = Math.max(0, Math.min(last, Math.round(proximalIndex)));
    const distal = Math.max(0, Math.min(last, Math.round(distalIndex)));
    const proximalLength = Number(lengthArray[proximal]);
    const distalLength = Number(lengthArray[distal]);

    if (Number.isFinite(proximalLength) && Number.isFinite(distalLength)) {
      const targetLength = (proximalLength + distalLength) / 2;
      const nearest = findNearestLengthIndex(
        lengthArray,
        targetLength,
        proximal,
        distal,
      );
      return clampLesionStackHiddenMiddleLocalIndex(
        nearest,
        proximal,
        distal,
        last,
      );
    }
  }

  return clampLesionStackHiddenMiddleLocalIndex(
    (proximalIndex + distalIndex) / 2,
    proximalIndex,
    distalIndex,
    lastIndex,
  );
}

const PlotPanel: React.FC<PanelCommonProps> = ({ className, panelConfig }) => {
  const divRef = useRef<HTMLDivElement | null>(null);
  const draggingKeyRef = useRef<MarkerKey>(null);

  const { globalToolState, selectedLayoutConfig } = useAnalysisPage();
  const {
    panelStates,
    fullscreenPanelComponentKey,
    selectedPanelClassName,
    analysisType,
    updateCPRState,
  } = usePanelContainer();
  const { selectedSeriesId } = useBrowserPage();
  const { config } = useConfig();
  const lesionStackSpacingMm = normalizeLesionStackSpacingMm(
    config.lesionStackSpacingMm,
  );
  const runtimeDisplayProfile = useMemo(() => getDisplayRuntimeProfile(), []);
  const graphXAccessor = useCallback((d: DataPoint) => d.x, []);
  const graphYAccessor = useCallback((d: DataPoint) => d.y, []);

  const isFFRMode = analysisType === 'FFR';
  const isLesionStackViewLayout =
    analysisType === 'plaque' && selectedLayoutConfig.type === 'lesionStackView';
  const isServerLesionStackViewLayout =
    isLesionStackViewLayout && getConfiguredCprCrossSectionImageMode() === 'server';
  const panelState = panelStates[panelConfig.componentKey];
  if (!panelState || !panelStates.CPR.coronary?.current) return null;

  const mainIndex = useMemo(() => {
    const [_, main] = className.split('-');
    return Number(main);
  }, [className]);

  const branchId = panelStates.CPR.coronary?.current[mainIndex]!;
  const panelBranchInfo = panelStates.CPR.coronary?.state[branchId]!;

  const { bufferPool } = useBufferPool();
  const pool = selectedSeriesId ? bufferPool[selectedSeriesId] : undefined;

  const centerlineData = pool?.centerData.loaded
    ? (pool?.centerData.data as Record<number, vtkPolyData>)[branchId!]
    : null;

  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [pmdNode, setPmdNode] = useState<ChartSelectedIndices>({
    proximal: -1,
    mid: -1,
    distal: -1,
  });
  const pmdNodeRef = useRef<ChartSelectedIndices>({
    proximal: -1,
    mid: -1,
    distal: -1,
  });
  const altGroupDraggingRef = useRef(false);
  const dragNodeIdRef = useRef(-1);
  const [dragNodeId, setDragNodeId] = useState(-1);
  const [draggingKey, setDraggingKey] = useState<MarkerKey>(null);
  const setSyncedPmdNode = (next: ChartSelectedIndices) => {
    pmdNodeRef.current = next;
    setPmdNode(next);
  };
  const setDragNodeIdSynced = (next: number) => {
    if (dragNodeIdRef.current === next) return;
    dragNodeIdRef.current = next;
    setDragNodeId(next);
  };
  const [ffrTransientIndex, setFfrTransientIndex] = useState(-1);

  const { updateSelectedPanelConfig } = usePanelContainer();

  const dataset = useMemo(() => {
    const raw = pool?.branchGraphDataset?.data ?? null;
    return raw instanceof BranchInfo ? raw : null;
  }, [pool?.branchGraphDataset?.data]);

  const lesionStackLocalIndices = useMemo(() => {
    if (!isLesionStackViewLayout) return [];
    const lengthArray = dataset?.getLengthArray(branchId);
    return buildLesionStackLocalIndices(
      lengthArray,
      pmdNode.proximal,
      pmdNode.mid,
      pmdNode.distal,
      lesionStackSpacingMm,
    );
  }, [
    dataset,
    branchId,
    isLesionStackViewLayout,
    pmdNode.proximal,
    pmdNode.mid,
    pmdNode.distal,
    lesionStackSpacingMm,
  ]);

  const lesionStackReferenceMarkers = useMemo(
    () =>
      lesionStackLocalIndices
        .map((idx, markerIndex) => {
          if (!Number.isFinite(idx)) return null;
          const isFirst = markerIndex === 0;
          const isLast = markerIndex === lesionStackLocalIndices.length - 1;
          return {
            idx: Number(idx),
            markerKey: isFirst
              ? ('proximal' as const)
              : isLast
                ? ('distal' as const)
                : undefined,
            dashed: !isFirst && !isLast,
            interactive: isFirst || isLast,
          };
        })
        .filter(
          (marker): marker is {
            idx: number;
            markerKey?: Exclude<MarkerKey, null>;
            dashed: boolean;
            interactive: boolean;
          } => marker !== null,
        ),
    [lesionStackLocalIndices],
  );

  const lesionStackSelectedIndices = useMemo<ChartSelectedIndices>(() => {
    if (!isLesionStackViewLayout || !lesionStackLocalIndices.length) return pmdNode;
    return {
      proximal: lesionStackLocalIndices[0] ?? pmdNode.proximal,
      mid: -1,
      distal:
        lesionStackLocalIndices[lesionStackLocalIndices.length - 1] ??
        pmdNode.distal,
    };
  }, [isLesionStackViewLayout, lesionStackLocalIndices, pmdNode]);

  const ffrGraphDataset = useMemo(() => {
    const raw = pool?.ffrGraphDataset?.data as
      | { byNode?: Record<number, FfrGraphNodeData> }
      | undefined;
    if (!raw?.byNode) return null;
    return raw;
  }, [pool?.ffrGraphDataset?.data]);

  const selectedFfrCenterlineId = config.ffr_selected_centerline_id;

  const selectedFfrNodeData = useMemo<FfrGraphNodeData | null>(() => {
    if (
      selectedFfrCenterlineId === null ||
      selectedFfrCenterlineId === undefined
    ) {
      return null;
    }
    const selected = ffrGraphDataset?.byNode?.[selectedFfrCenterlineId] ?? null;
    return selected;
  }, [ffrGraphDataset, selectedFfrCenterlineId]);

  const rendererId = analysisType === 'fai' ? className : 'Graph';

  renderers[rendererId] = {
    render: (args?: any) => {
      const { type } = args || {};
      if (type === 'markerMove' || type === 'polygonMove') {
        const { which, newIndex, newPickedPoints } = args;
        const next = { ...pmdNodeRef.current };
        let curindex = newIndex;
        if (curindex < 0) curindex = 0;

        if (which === 'total' && newPickedPoints) {
          const updatepickpoint: {
            position: [number, number, number];
            normal: [number, number, number];
            index: number;
          }[] = [];

          for (let i = 0; i < newPickedPoints.length; i++) {
            if (newPickedPoints[i] === null) return;
            if (i === 0) next.proximal = newPickedPoints[i].index;
            else if (i === 1) next.mid = newPickedPoints[i].index;
            else if (i === 2) next.distal = newPickedPoints[i].index;

            updatepickpoint.push({
              position: newPickedPoints[i].position,
              normal: newPickedPoints[i].normal,
              index: newPickedPoints[i].index + config.coronary_front_expand,
            });
          }

          if (isServerLesionStackViewLayout && !isFFRMode) {
            const hiddenMiddleIndex = resolveLesionStackHiddenMiddleLocalIndex(
              dataset?.getLengthArray(branchId),
              next.proximal,
              next.distal,
              dataset?.getLengthArray(branchId)?.length ?? 0,
            );
            if (hiddenMiddleIndex !== null) {
              next.mid = hiddenMiddleIndex;
            }
          }

          updateCPRState({ indexedStates: { [branchId]: updatepickpoint } });
        } else {
          if (isFFRMode) {
            next.mid = curindex;
          } else {
            if (which === 0) next.proximal = curindex;
            if (which === 1) next.mid = curindex;
            if (which === 2) next.distal = curindex;
          }

          if (
            isServerLesionStackViewLayout &&
            !isFFRMode &&
            (which === 0 || which === 2)
          ) {
            const hiddenMiddleIndex = resolveLesionStackHiddenMiddleLocalIndex(
              dataset?.getLengthArray(branchId),
              next.proximal,
              next.distal,
              dataset?.getLengthArray(branchId)?.length ?? 0,
            );
            if (hiddenMiddleIndex !== null) {
              next.mid = hiddenMiddleIndex;
            }
          }

          updateCPRState({
            indexedState: {
              mainIndex: {
                branchId,
                index: isFFRMode ? 0 : which,
              },
              pickPoint: { index: curindex + config.coronary_front_expand },
            },
          });
        }

        setSyncedPmdNode(next);
      }
    },
  };

  useEffect(() => {
    if (!divRef.current) return;

    const currentDiv = divRef.current;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        setDimensions({ width, height });
      }
    });

    observer.observe(currentDiv);
    return () => observer.unobserve(currentDiv);
  }, []);

  useEffect(() => {
    if (!Array.isArray(panelBranchInfo) || panelBranchInfo.length < 1) return;

    if (isFFRMode) {
      const idx = panelBranchInfo[0].index - config.coronary_front_expand;
      if (pmdNode.mid === idx) return;
      setSyncedPmdNode({ proximal: -1, mid: idx, distal: -1 });
      return;
    }

    if (panelBranchInfo.length < 3) return;

    const [x, y, z] = panelBranchInfo;
    if (
      pmdNode.proximal === x.index - config.coronary_front_expand &&
      pmdNode.mid === y.index - config.coronary_front_expand &&
      pmdNode.distal === z.index - config.coronary_front_expand
    ) {
      return;
    }

    setSyncedPmdNode({
      proximal: x.index - config.coronary_front_expand,
      mid: y.index - config.coronary_front_expand,
      distal: z.index - config.coronary_front_expand,
    });
  }, [
    branchId,
    isFFRMode,
    panelBranchInfo?.[0]?.index,
    panelBranchInfo?.[1]?.index,
    panelBranchInfo?.[2]?.index,
    config.coronary_front_expand,
  ]);

  useEffect(() => {
    if (!isFFRMode) return;
    setFfrTransientIndex(-1);
  }, [isFFRMode, branchId, selectedFfrCenterlineId]);

  useEffect(()=>{
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Alt') {
        e.preventDefault();
        d3.selectAll('.marker-group').classed('is-all-hovered', true);
      }
    };
    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Alt') {
        d3.selectAll('.marker-group').classed('is-all-hovered', false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return ()=>{
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);

    }
  },[])
  const lumenArea_data = useMemo(() => {
    if (!dataset) return [];
    const arr = dataset.getLumenArea(branchId);
    const length = dataset.getLengthArray(branchId);
    if (!arr || !length) return [];
    return Array.from({ length: arr.length }, (_, i) => ({
      x: length[i],
      y: arr[i],
    }));
  }, [dataset, branchId]);

  const hu_Data = useMemo(() => {
    if (!dataset) return [];
    const arr = dataset.getHUArray(branchId);
    const length = dataset.getLengthArray(branchId);
    if (!arr || !length) return [];
    return Array.from({ length: arr.length }, (_, i) => ({
      x: length[i],
      y: arr[i],
    }));
  }, [dataset, branchId]);

  const lumenDiameter_Data = useMemo(() => {
    if (!dataset) return [];
    const arr = dataset.getLumenDiameter(branchId);
    const length = dataset.getLengthArray(branchId);
    if (!arr || !length) return [];
    return Array.from({ length: arr.length }, (_, i) => ({
      x: length[i],
      y: arr[i],
    }));
  }, [dataset, branchId]);

  const ffrData = useMemo(() => {
    if (!isFFRMode) return [];
    if (selectedFfrNodeData?.ffr?.length) return selectedFfrNodeData.ffr;
    return [];
  }, [isFFRMode, selectedFfrNodeData]);

  const diameterChartData = useMemo(() => {
    if (isFFRMode) {
      if (selectedFfrNodeData?.diameter?.length)
        return selectedFfrNodeData.diameter;
      return [];
    }
    return lumenDiameter_Data;
  }, [isFFRMode, selectedFfrNodeData, lumenDiameter_Data]);

  const areaChartData = useMemo(() => {
    if (isFFRMode) {
      if (selectedFfrNodeData?.area?.length) return selectedFfrNodeData.area;
      return [];
    }
    return lumenArea_data;
  }, [isFFRMode, selectedFfrNodeData, lumenArea_data]);

  const diameterYDomain = useMemo<[number, number]>(() => {
    if (isFFRMode) {
      return getFiniteDomain(diameterChartData, [2.2, 4.2]);
    }
    return [
      dataset?.getStats(branchId)?.lumenDiameter.min ?? 0,
      dataset?.getStats(branchId)?.lumenDiameter.max ?? 1,
    ];
  }, [isFFRMode, diameterChartData, dataset, branchId]);

  const areaYDomain = useMemo<[number, number]>(() => {
    if (isFFRMode) {
      return getFiniteDomain(areaChartData, [2.4, 4.6]);
    }
    return [
      dataset?.getStats(branchId)?.lumenArea.min ?? 0,
      dataset?.getStats(branchId)?.lumenArea.max ?? 1,
    ];
  }, [isFFRMode, areaChartData, dataset, branchId]);

  const histogramProps = useMemo(() => {
    if (!dataset) return { data: [], mean: 0, sd: 1 };
    const start = pmdNode.proximal;
    const end = pmdNode.distal;
    const volumeArray =
      dataset.getFAIHUCountAndVolume?.(branchId, start, end)?.volumeArray ?? [];
    const totalInfo = dataset.getFAITotalInfo?.(branchId, start, end);
    return {
      data: volumeArray,
      mean: typeof totalInfo?.faimean === 'number' ? totalInfo.faimean : 0,
      sd: typeof totalInfo?.faisd === 'number' ? totalInfo.faisd : 1,
    };
  }, [pmdNode, dataset, branchId]);

  const plaqueProps = useMemo(() => {
    const lengthArr = dataset?.getLengthArray(branchId) ?? [];
    const lumenArr = dataset?.getLumenArea(branchId) ?? new Float32Array();
    const wallArr = dataset?.getWallArea(branchId) ?? new Float32Array();
    const plaqueinfo = dataset?.getPlaqueArea(branchId);
    const lowArr = plaqueinfo?.low ?? [];
    const noncalcArr = plaqueinfo?.noncalc ?? [];
    const calcArr = plaqueinfo?.calc ?? [];
    return { lengthArr, lumenArr, wallArr, lowArr, noncalcArr, calcArr };
  }, [dataset, branchId]);

  const bottomLeftContent = useMemo(
    () => (
      <div className={`projection-text ${panelConfig.componentKey}`}>
        {panelConfig.componentKey}
      </div>
    ),
    [panelConfig.componentKey],
  );

  const memoizedClassName = useMemo(() => {
    const isFullscreen =
      fullscreenPanelComponentKey === panelConfig.componentKey;
    const isSelected = selectedPanelClassName === className;
    return `panel ${className}${isFullscreen && isSelected ? ' fullscreen' : ''}`;
  }, [
    fullscreenPanelComponentKey,
    selectedPanelClassName,
    className,
    panelConfig.componentKey,
  ]);

  const maxIndex = useMemo(() => {
    if (!dataset) return 0;
    const length = dataset.getLengthArray(branchId)?.length;
    try {
      return length ?? 0;
    } catch {
      return 0;
    }
  }, [dataset, branchId, centerlineData]);

  const buildPickedPointForLocalIndex = (idx: number) => {
    const fullIndex = idx + config.coronary_front_expand;
    if (centerlineData) {
      const nodedata = handleNodeChange(centerlineData, fullIndex);
      if (nodedata !== null) {
        const { position, normal } = nodedata;
        return { position, normal, index: fullIndex };
      }
    }
    return { index: fullIndex };
  };

  const commitGraphMarkerState = (
    next: ChartSelectedIndices,
    pickedPoints?: ReturnType<typeof buildPickedPointForLocalIndex>[],
  ) => {
    if (
      !Number.isFinite(next.proximal) ||
      !Number.isFinite(next.mid) ||
      !Number.isFinite(next.distal)
    ) {
      return;
    }

    updateCPRState({
      indexedStates: {
        [branchId]:
          pickedPoints ??
          [next.proximal, next.mid, next.distal].map(
            buildPickedPointForLocalIndex,
          ),
      },
    });
  };

  const syncAllGraphMarkers = (next: ChartSelectedIndices) => {
    if (!Number.isFinite(next.proximal) || !Number.isFinite(next.mid) || !Number.isFinite(next.distal)) {
      return;
    }

    const localIndices = [next.proximal, next.mid, next.distal];
    const fullIndices = localIndices.map(
      (localIndex) => localIndex + config.coronary_front_expand,
    );
    const fullPickedPoints = localIndices.map(buildPickedPointForLocalIndex);
    const linkedPickedPoints = fullPickedPoints.map((pickedPoint, index) => ({
      ...pickedPoint,
      index: localIndices[index],
    }));

    (['stretched', 'straightened'] as const).forEach((modeName) => {
      scheduleRender(`CPR.${mainIndex}.${modeName}`, {
        type: 'polygonMove',
        which: 'total',
        newPickedPoints: fullPickedPoints,
      });
      scheduleRender(`MarkerLine.${mainIndex}.${modeName}`, {
        type: 'polygonMove',
        pickPoints: fullIndices,
      });
    });

    const crossSectionSlots = isLesionStackViewLayout
      ? buildLesionStackLocalIndices(
          dataset?.getLengthArray(branchId),
          next.proximal,
          next.mid,
          next.distal,
          lesionStackSpacingMm,
        )
          .map((slotIndex, slot) => ({ slotIndex, slot }))
          .filter(({ slotIndex }) => Number.isFinite(slotIndex))
      : localIndices.map((slotIndex, slot) => ({ slotIndex, slot }));

    const isCrossSectionGroupPreview = crossSectionSlots.length > 1;
    crossSectionSlots.forEach(({ slotIndex, slot }) => {
      const rendererId = isLesionStackViewLayout
        ? getLesionStackCrossSectionRendererId(mainIndex, slot)
        : `CrossSection.${mainIndex}.${slot}`;
      scheduleRender(rendererId, {
        type: 'markerMove',
        which: slot,
        newIndex: Number(slotIndex) + config.coronary_front_expand,
        markerGroupPreview: isCrossSectionGroupPreview,
      });
    });

    scheduleRender('Report', {
      type: 'polygonMove',
      mainIndex,
      which: 'total',
      newPickedPoints: linkedPickedPoints,
    });

    const linkedPickedPointsWithGeometry = linkedPickedPoints.filter(
      (pickedPoint) => pickedPoint.position && pickedPoint.normal,
    );
    if (linkedPickedPointsWithGeometry.length === linkedPickedPoints.length) {
      scheduleRender('Surface3D', {
        type: 'polygonMove',
        mainIndex,
        which: 'total',
        newPickedPoints: linkedPickedPointsWithGeometry,
      });
      scheduleRender('VR', {
        type: 'polygonMove',
        mainIndex,
        which: 'total',
        newPickedPoints: linkedPickedPointsWithGeometry,
      });
    }
  };

  const applyIndex = (e: PointerEvent, idx: number, whichKey: MarkerKey) => {
    if (!Number.isFinite(idx)) return;
    if (idx < 0 || idx >= maxIndex) return;

    if (whichKey === null) {
      setDragNodeIdSynced(idx);
      return;
    }

    const shouldMoveAllMarkers = e.altKey || altGroupDraggingRef.current;
    if (shouldMoveAllMarkers) {
      altGroupDraggingRef.current = true;
      const next = { ...pmdNodeRef.current };
      let inter = 0;
      if (!pool) return;

      if (whichKey === 'proximal') inter = idx - next.proximal;
      if (whichKey === 'mid') inter = idx - next.mid;
      if (whichKey === 'distal') inter = idx - next.distal;

      if (inter === 0) {
        setDragNodeIdSynced(idx);
        return;
      }

      if (
        next.proximal + inter < 0 ||
        next.mid + inter < 0 ||
        next.distal + inter < 0
      ) {
        return;
      }

      if (
        next.proximal + inter >= maxIndex ||
        next.mid + inter >= maxIndex ||
        next.distal + inter >= maxIndex
      ) {
        return;
      }

      next.proximal += inter;
      next.mid += inter;
      next.distal += inter;

      if (isServerLesionStackViewLayout) {
        const hiddenMiddleIndex = resolveLesionStackHiddenMiddleLocalIndex(
          dataset?.getLengthArray(branchId),
          next.proximal,
          next.distal,
          maxIndex,
        );
        if (hiddenMiddleIndex !== null) {
          next.mid = hiddenMiddleIndex;
        }
      }

      setDragNodeIdSynced(idx);
      setSyncedPmdNode(next);
      syncAllGraphMarkers(next);
      return;
    }

    if (pmdNodeRef.current[whichKey] === idx) {
      setDragNodeIdSynced(idx);
      return;
    }

    const next = { ...pmdNodeRef.current };
    let which = -1;

    if (whichKey === 'proximal') {
      next.proximal = idx;
      which = 0;
    }
    if (whichKey === 'mid') {
      next.mid = idx;
      which = 1;
    }
    if (whichKey === 'distal') {
      next.distal = idx;
      which = 2;
    }
    if (which < 0) return;

    let hiddenMiddlePickedPoint: ReturnType<typeof buildPickedPointForLocalIndex> | null = null;

    if (isLesionStackViewLayout) {
      if (next.proximal >= next.distal) return;
      if (
        which === 1 &&
        !isLesionStackMiddleAnchorLocalAllowed(
          dataset?.getLengthArray(branchId),
          next.proximal,
          next.mid,
          next.distal,
          lesionStackSpacingMm,
        )
      ) {
        return;
      }
      if (isServerLesionStackViewLayout && (which === 0 || which === 2)) {
        const hiddenMiddleIndex = resolveLesionStackHiddenMiddleLocalIndex(
          dataset?.getLengthArray(branchId),
          next.proximal,
          next.distal,
          maxIndex,
        );
        if (hiddenMiddleIndex !== null) {
          next.mid = hiddenMiddleIndex;
          hiddenMiddlePickedPoint = buildPickedPointForLocalIndex(hiddenMiddleIndex);
        }
      }
    } else if (next.mid <= next.proximal || next.mid >= next.distal) {
      return;
    }

    const fullIndex = idx + config.coronary_front_expand;
    const pickedPoint = buildPickedPointForLocalIndex(idx);

    setDragNodeIdSynced(idx);
    setSyncedPmdNode(next);

    (['stretched', 'straightened'] as const).forEach((modeName) => {
      scheduleRender(`CPR.${mainIndex}.${modeName}`, {
        type: 'markerMove',
        which,
        newIndices: fullIndex,
        newPickedPoints: [pickedPoint],
      });
      scheduleRender(`MarkerLine.${mainIndex}.${modeName}`, {
        type: 'markerMove',
        index: fullIndex,
        which,
      });
    });

    const crossSectionSlots = isLesionStackViewLayout
      ? buildLesionStackLocalIndices(
          dataset?.getLengthArray(branchId),
          next.proximal,
          next.mid,
          next.distal,
          lesionStackSpacingMm,
        )
          .map((slotIndex, slot) => ({ slotIndex, slot }))
          .filter(({ slotIndex }) => Number.isFinite(slotIndex))
      : [{ slotIndex: idx, slot: which }];
    const isCrossSectionGroupPreview = crossSectionSlots.length > 1;
    crossSectionSlots.forEach(({ slotIndex, slot }) => {
      const rendererId = isLesionStackViewLayout
        ? getLesionStackCrossSectionRendererId(mainIndex, slot)
        : `CrossSection.${mainIndex}.${slot}`;
      scheduleRender(rendererId, {
        type: 'markerMove',
        which,
        newIndex: Number(slotIndex) + config.coronary_front_expand,
        markerGroupPreview: isCrossSectionGroupPreview,
      });
    });

    scheduleRender('Report', {
      type: 'markerMove',
      mainIndex,
      which,
      newIndex: idx,
    });

    if (pickedPoint.position && pickedPoint.normal) {
      const linkedPickedPoint = {
        position: pickedPoint.position,
        normal: pickedPoint.normal,
        index: idx,
      };

      scheduleRender('Surface3D', {
        type: 'markerMove',
        mainIndex,
        which,
        newPickedPoints: [linkedPickedPoint],
      });

      scheduleRender('VR', {
        type: 'markerMove',
        mainIndex,
        which,
        newPickedPoints: [linkedPickedPoint],
      });
    }

    if (
      hiddenMiddlePickedPoint?.position &&
      hiddenMiddlePickedPoint?.normal
    ) {
      const hiddenLinkedPickedPoint = {
        position: hiddenMiddlePickedPoint.position,
        normal: hiddenMiddlePickedPoint.normal,
        index: next.mid,
      };

      scheduleRender('Surface3D', {
        type: 'markerMove',
        mainIndex,
        which: 1,
        newPickedPoints: [hiddenLinkedPickedPoint],
      });

      scheduleRender('VR', {
        type: 'markerMove',
        mainIndex,
        which: 1,
        newPickedPoints: [hiddenLinkedPickedPoint],
      });
    }
  };

  const onMarkerMouseDown = (key: MarkerKey) => {
    altGroupDraggingRef.current = false;
    draggingKeyRef.current = key;
    setDraggingKey(key);
  };

  const onDragStart = (e: PointerEvent, idx: number) => {
    applyIndex(e, idx, draggingKeyRef.current);
  };

  const onDrag = (e: PointerEvent, idx: number) => {
    applyIndex(e, idx, draggingKeyRef.current);
  };

  const onDragEnd = () => {
    setDragNodeIdSynced(-1);
    draggingKeyRef.current = null;
    setDraggingKey(null);

    const currentPmdNode = pmdNodeRef.current;
    const wasAltGroupDragging = altGroupDraggingRef.current;
    altGroupDraggingRef.current = false;

    if (isFFRMode) {
      MarkerLinesMoveRender(
        0,
        branchId,
        [0],
        [currentPmdNode.mid + config.coronary_front_expand],
        pool!,
      );
      return;
    }

    commitGraphMarkerState(currentPmdNode);

    if (wasAltGroupDragging) {
      syncAllGraphMarkers(currentPmdNode);
      return;
    }

    MarkerLinesMoveRender(
      0,
      branchId,
      [0, 1, 2],
      [
        currentPmdNode.proximal + config.coronary_front_expand,
        currentPmdNode.mid + config.coronary_front_expand,
        currentPmdNode.distal + config.coronary_front_expand,
      ],
      pool!,
    );
  };

  const onFfrTransientIndexStart = (idx: number) => {
    setFfrTransientIndex(idx);
    scheduleRender('Surface3D', {
      type: 'markerMove',
      idx,
      branchId:selectedFfrCenterlineId,
      ffrData:ffrData[idx].y,
    });

  };

  const onFfrTransientIndexChange = (idx: number) => {
    setFfrTransientIndex(idx);
    scheduleRender('Surface3D', {
      type: 'markerMove',
      idx,
      ffrData:ffrData[idx].y,
      branchId:selectedFfrCenterlineId,
    });
  };

  const onFfrTransientIndexEnd = () => {
    setFfrTransientIndex(-1);
    scheduleRender('Surface3D', {
      type: 'markerMove',
      idx:-1,
      branchId:selectedFfrCenterlineId,
    });
  };

  const isSingleDiameterAreaGraph =
    analysisType === 'stenosis' || isLesionStackViewLayout;
  const stenosisPadding = isSingleDiameterAreaGraph ? 8 : 0;
  const ffrUiScale = runtimeDisplayProfile.ffrChartScale;
  const ffrMargin = useMemo(
    () => ({
      top: Math.round(FFR_SHARED_MARGIN.top * ffrUiScale),
      right: Math.round(FFR_SHARED_MARGIN.right * ffrUiScale),
      bottom: Math.round(FFR_SHARED_MARGIN.bottom * ffrUiScale),
      left: Math.round(FFR_SHARED_MARGIN.left * ffrUiScale),
    }),
    [ffrUiScale],
  );
  const ffrDualLineMargin = useMemo(
    () => ({
      ...ffrMargin,
      bottom: ffrMargin.bottom + Math.round(12 * ffrUiScale),
    }),
    [ffrMargin, ffrUiScale],
  );

  const dualLineHeight =
    isSingleDiameterAreaGraph
      ? Math.max(0, dimensions.height - stenosisPadding * 2)
      : dimensions.height / 2;

  const dualLineWidth =
    isSingleDiameterAreaGraph
      ? Math.max(0, dimensions.width - stenosisPadding * 2)
      : dimensions.width;

  return (
    <div
      className={memoizedClassName}
      onClick={() => {
        updateSelectedPanelConfig({
          ...panelConfig,
          componentKey: panelConfig.componentKey,
          className,
        });
      }}
      ref={divRef}
    >
      <div className={`renderer Plot-${mainIndex}`}>
        {panelConfig.componentKey === 'Histogram' ? (
          <HistogramChart
            mainIndex={mainIndex}
            data={histogramProps.data}
            width={dimensions.width}
            height={dimensions.height}
            yLabel="Volume"
            xLabel="HU"
            mean={histogramProps.mean}
            sd={histogramProps.sd}
            smoothWindow={7}
            lineWidth={1}
          />
        ) : (
          <div
            className={`${styles.graphPanel} ${
              isSingleDiameterAreaGraph ? styles.stenosisGraph : ''
            }`}
          >
            {isFFRMode && (
              <FFRChart
                width={dimensions.width}
                height={dimensions.height / 2}
                data={ffrData}
                currentIndex={ffrTransientIndex}
                title="FFR"
                margin={ffrMargin}
                uiScale={ffrUiScale}
                onTransientIndexStart={onFfrTransientIndexStart}
                onTransientIndexChange={onFfrTransientIndexChange}
                onTransientIndexEnd={onFfrTransientIndexEnd}
              />
            )}

            <DualLineChart
              lineA={{
                data: areaChartData,
                y: graphYAccessor,
                yDomain: areaYDomain,
                yLabel: 'Area (mm²)',
                color: 'orange',
                strokeWidth: 1,
              }}
              lineB={{
                data: diameterChartData,
                y: graphYAccessor,
                yDomain: diameterYDomain,
                yLabel: 'Diameter (mm)',
                color: 'white',
                strokeWidth: 1,
              }}
              x={graphXAccessor}
              width={dualLineWidth}
              height={isFFRMode ? dimensions.height / 2 : dualLineHeight}
              title="Diameter & Area Graph"
              selectedIndices={
                isFFRMode
                  ? { proximal: -1, mid: ffrTransientIndex, distal: -1 }
                  : lesionStackSelectedIndices
              }
              referenceMarkers={
                isLesionStackViewLayout ? lesionStackReferenceMarkers : undefined
              }
              dragNodeId={dragNodeId}
              draggingKey={draggingKey}
              onMarkerMouseDown={onMarkerMouseDown}
              onDragStart={onDragStart}
              onDrag={onDrag}
              onDragEnd={onDragEnd}
              sharedMargin={isFFRMode ? ffrDualLineMargin : undefined}
              interactiveMarkers={!isFFRMode}
              uiScale={isFFRMode ? ffrUiScale : 1}
              onTransientIndexStart={
                isFFRMode ? onFfrTransientIndexStart : undefined
              }
              onTransientIndexChange={
                isFFRMode ? onFfrTransientIndexChange : undefined
              }
              onTransientIndexEnd={isFFRMode ? onFfrTransientIndexEnd : undefined}
            />

            {analysisType === 'plaque' &&
              !isFFRMode &&
              !isLesionStackViewLayout && (
              <PlaqueChart
                {...plaqueProps}
                width={dimensions.width}
                height={dimensions.height / 2}
                selectedIndices={pmdNode}
                dragNodeId={dragNodeId}
                draggingKey={draggingKey}
                onMarkerMouseDown={onMarkerMouseDown}
                onDragStart={onDragStart}
                onDrag={onDrag}
                onDragEnd={onDragEnd}
              />
            )}

            {analysisType === 'isr' && !isFFRMode && (
              <LineChart
                data={hu_Data}
                x={graphXAccessor}
                y={graphYAccessor}
                yDomain={[
                  dataset?.getStats(branchId)?.hu.min!,
                  dataset?.getStats(branchId)?.hu.max!,
                ]}
                title="HU Graph"
                width={dimensions.width}
                height={dimensions.height / 2}
                yLabel="HU"
                color="white"
                strokeWidth={2}
                selectedIndices={pmdNode}
                dragNodeId={dragNodeId}
                draggingKey={draggingKey}
                onMarkerMouseDown={onMarkerMouseDown}
                onDragStart={onDragStart}
                onDrag={onDrag}
                onDragEnd={onDragEnd}
              />
            )}
          </div>
        )}
      </div>

      {globalToolState?.toggles.ToggleAnnotations && (
        <PanelOverlay
          config={{ 'bottom-left': { content: bottomLeftContent } }}
          originComponentKey={panelConfig.componentKey}
          className={className}
        />
      )}
    </div>
  );
};

export default PlotPanel;
